export const ACTIVITY_SCROLL_ANCHOR_KEY = 'katafit:update-activity-scroll-anchor';
export const MIN_RESTORED_ACTIVITY_TOP = 96;
const ANCHOR_TTL_MS = 10 * 60 * 1000;
const MAX_RESTORE_PAGES = 1000;

export function getActivityScrollId(activity) {
  return activity?._virtualId || activity?._id || null;
}

export async function waitForActivityLoadIdle({
  isLoading,
  isCancelled = () => false,
  expiresAt,
  now = Date.now,
  delay,
}) {
  while (isLoading()) {
    if (isCancelled()) return 'cancelled';
    if (now() >= expiresAt) return 'expired';
    await delay();
  }
  if (isCancelled()) return 'cancelled';
  if (now() >= expiresAt) return 'expired';
  return 'idle';
}

const safeRemove = storage => {
  try {
    storage?.removeItem?.(ACTIVITY_SCROLL_ANCHOR_KEY);
  } catch (_) {
    // Storage can be unavailable in private/restricted browser contexts.
  }
};

export function routeIdentity(locationRef = window.location) {
  try {
    const params = new URLSearchParams(locationRef?.search || '');
    params.delete('__appv');
    const query = params.toString();
    return `${locationRef?.pathname || '/'}${query ? `?${query}` : ''}`;
  } catch (_) {
    return locationRef?.pathname || '/';
  }
}

export function captureActivityScrollAnchor({
  documentRef = document,
  windowRef = window,
  storage,
  locationRef = window.location,
  now = Date.now(),
} = {}) {
  try {
    const targetStorage = storage || windowRef?.sessionStorage;
    const viewportHeight = Number(windowRef?.innerHeight) || 0;
    const rows = Array.from(documentRef?.querySelectorAll?.('[data-scroll-activity-id]') || []);
    const visible = rows
      .map(element => {
        const rect = element.getBoundingClientRect();
        return {
          element,
          rect,
          visibleHeight: Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)),
        };
      })
      .filter(({ element, rect, visibleHeight }) => Boolean(element?.dataset?.scrollActivityId)
        && Number.isFinite(rect?.top)
        && Number.isFinite(rect?.bottom)
        && visibleHeight > 0)
      // A prior row can remain clipped by only a few pixels at the top. It is
      // technically visible, but it is not the activity occupying the user's
      // attention. Anchor the row with the greatest viewport presence instead.
      .sort((a, b) => b.visibleHeight - a.visibleHeight || a.rect.top - b.rect.top)[0];

    if (!visible) return null;
    const anchor = {
      activityId: String(visible.element.dataset.scrollActivityId),
      // Expanded-card state is intentionally not persisted through a deployment.
      // Never replay a deeply negative top from a tall expanded row onto its
      // collapsed replacement, or the target activity can land fully offscreen.
      viewportTop: Math.max(visible.rect.top, MIN_RESTORED_ACTIVITY_TOP),
      route: routeIdentity(locationRef),
      expiresAt: now + ANCHOR_TTL_MS,
    };
    targetStorage?.setItem?.(ACTIVITY_SCROLL_ANCHOR_KEY, JSON.stringify(anchor));
    return anchor;
  } catch (_) {
    return null;
  }
}

export function readActivityScrollAnchor({
  storage,
  windowRef = window,
  locationRef = window.location,
  now = Date.now(),
} = {}) {
  let anchor;
  let targetStorage;
  try {
    targetStorage = storage || windowRef?.sessionStorage;
    const raw = targetStorage?.getItem?.(ACTIVITY_SCROLL_ANCHOR_KEY);
    if (!raw) return null;
    anchor = JSON.parse(raw);
  } catch (_) {
    safeRemove(targetStorage);
    return null;
  }

  const valid = anchor
    && typeof anchor.activityId === 'string'
    && anchor.activityId.length > 0
    && Number.isFinite(anchor.viewportTop)
    && Number.isFinite(anchor.expiresAt)
    && anchor.expiresAt > now
    && anchor.route === routeIdentity(locationRef);
  if (!valid) {
    safeRemove(targetStorage);
    return null;
  }
  return anchor;
}

export function clearActivityScrollAnchor(storage) {
  try {
    safeRemove(storage || window?.sessionStorage);
  } catch (_) {
    // Access to sessionStorage itself can be denied.
  }
}

export function findActivityScrollElement(activityId, documentRef = document) {
  try {
    return Array.from(documentRef?.querySelectorAll?.('[data-scroll-activity-id]') || [])
      .find(element => String(element?.dataset?.scrollActivityId) === String(activityId)) || null;
  } catch (_) {
    return null;
  }
}

export async function restoreActivityScrollAnchor({
  anchor,
  findActivity,
  loadNextPage,
  hasMore,
  waitForLayout,
  scrollBy,
  clear,
  isCancelled = () => false,
  isExpired = () => false,
  maxPages = MAX_RESTORE_PAGES,
}) {
  let pagesLoaded = 0;
  while (!isCancelled()) {
    if (isExpired()) {
      clear();
      return 'expired';
    }
    await waitForLayout();
    if (isCancelled()) return 'cancelled';
    if (isExpired()) {
      clear();
      return 'expired';
    }

    const element = findActivity(anchor.activityId);
    if (element) {
      const currentTop = element.getBoundingClientRect().top;
      if (isExpired()) {
        clear();
        return 'expired';
      }
      const delta = currentTop - anchor.viewportTop;
      if (Number.isFinite(delta) && Math.abs(delta) > 0.5) scrollBy(delta);
      clear();
      return 'restored';
    }

    if (!hasMore()) {
      clear();
      return 'unreachable';
    }
    if (pagesLoaded >= maxPages) {
      clear();
      return 'bounded';
    }

    const progressed = await loadNextPage();
    if (isCancelled()) return 'cancelled';
    if (!progressed) {
      clear();
      return 'stalled';
    }
    pagesLoaded += 1;
  }
  return 'cancelled';
}
